/**
 * Census Eligibility Dashboard Widget
 * 
 * Shows census-wide hospice eligibility status at a glance:
 * - Distribution of patients by eligibility status
 * - LCD category breakdown
 * - Active alerts summary
 * - Trend indicators
 */

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line, Area, AreaChart
} from "recharts";
import {
  Users, Activity, AlertTriangle, TrendingDown, TrendingUp,
  CheckCircle, Clock, ChevronRight, RefreshCw, Loader2,
  BarChart3, PieChart as PieChartIcon, Filter
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const COLORS = {
  eligible: "#f59e0b",      // Amber - hospice eligible
  approaching: "#3b82f6",   // Blue - approaching threshold
  above: "#10b981",         // Emerald - above threshold
  critical: "#ef4444",      // Red - critical alerts
  high: "#f97316"           // Orange - high alerts
};

// LCD Category colors
const LCD_COLORS = {
  cancer: "#ec4899",
  dementia: "#a855f7",
  heart_disease: "#ef4444",
  pulmonary: "#3b82f6",
  renal: "#f59e0b",
  other: "#6b7280"
};

export default function CensusEligibilityWidget({ 
  isDark = true, 
  compact = false,
  showTrends = true,
  userRole = "administrator",  // Role-based filtering
  currentPatientId = null,     // Current patient context
  currentPatientName = null
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // Check if user is staff (limited view)
  const isStaffView = userRole === "staff" || userRole === "nurse" || userRole === "coordinator";
  
  // States
  const [summary, setSummary] = useState(null);
  const [lcdBreakdown, setLcdBreakdown] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [patientList, setPatientList] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientEligibility, setPatientEligibility] = useState(null); // For staff - single patient view

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Staff only sees current patient's eligibility
        if (isStaffView && currentPatientId) {
          // Fetch patient-specific eligibility
          const patientRes = await fetch(`${API_URL}/api/census-eligibility/patient/${currentPatientId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (patientRes.ok) {
            setPatientEligibility(await patientRes.json());
          } else {
            // Fallback demo data for patient
            setPatientEligibility({
              patient_id: currentPatientId,
              patient_name: currentPatientName || "Current Patient",
              pps: 60,
              eligibility_status: "approaching_threshold",
              days_to_threshold: 14,
              lcd_category: "cancer",
              last_assessment: new Date().toISOString()
            });
          }
          setLoading(false);
          return;
        }
        
        // Admin/Manager sees census-wide data
        // Fetch summary
        const summaryRes = await fetch(`${API_URL}/api/census-eligibility/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (summaryRes.ok) {
          setSummary(await summaryRes.json());
        }
        
        // Fetch LCD breakdown
        const lcdRes = await fetch(`${API_URL}/api/census-eligibility/lcd-breakdown`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (lcdRes.ok) {
          const data = await lcdRes.json();
          setLcdBreakdown(data.breakdown || []);
        }
        
        // Fetch trends
        if (showTrends) {
          const trendsRes = await fetch(`${API_URL}/api/census-eligibility/trends?days=30`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (trendsRes.ok) {
            const data = await trendsRes.json();
            setTrends(data.trends || []);
          }
        }
        
      } catch (error) {
        console.error("Failed to fetch census data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, showTrends, isStaffView, currentPatientId]);

  // Fetch patients by status
  const fetchPatientsByStatus = async (status) => {
    try {
      setLoadingPatients(true);
      setSelectedStatus(status);
      
      const res = await fetch(
        `${API_URL}/api/census-eligibility/patients-by-status?status=${status}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setPatientList(data.patients || []);
      }
    } catch (error) {
      console.error("Failed to fetch patients:", error);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Prepare pie chart data
  const pieData = useMemo(() => {
    if (!summary) return [];
    const dist = summary.eligibility_distribution;
    return [
      { name: "Hospice Eligible", value: dist.hospice_eligible, color: COLORS.eligible },
      { name: "Approaching", value: dist.approaching_threshold, color: COLORS.approaching },
      { name: "Above Threshold", value: dist.above_threshold, color: COLORS.above }
    ];
  }, [summary]);

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <div className={`p-2 rounded shadow-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
        <p className="text-sm font-medium" style={{ color: data.color }}>{data.name}</p>
        <p className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
          {data.value} patients
        </p>
      </div>
    );
  };

  // Staff compact view - shows only current patient's eligibility
  if (compact && isStaffView) {
    return (
      <div className="space-y-3" data-testid="patient-eligibility-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Patient Eligibility
            </span>
          </div>
        </div>

        {loading ? (
          <div className={`p-4 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
          </div>
        ) : patientEligibility ? (
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            {/* Patient name */}
            <p className={`text-sm font-medium mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
              {patientEligibility.patient_name || currentPatientName || "Current Patient"}
            </p>
            
            {/* PPS Score */}
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>PPS Score</span>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-bold ${
                  patientEligibility.pps <= 70 ? "text-amber-400" : "text-emerald-400"
                }`}>
                  {patientEligibility.pps}%
                </span>
                {patientEligibility.pps <= 70 && (
                  <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                    Hospice Eligible
                  </Badge>
                )}
              </div>
            </div>

            {/* Eligibility Status */}
            <div className={`p-2 rounded border ${
              patientEligibility.eligibility_status === "hospice_eligible" 
                ? "bg-amber-500/10 border-amber-500/30" 
                : patientEligibility.eligibility_status === "approaching_threshold"
                  ? "bg-blue-500/10 border-blue-500/30"
                  : "bg-emerald-500/10 border-emerald-500/30"
            }`}>
              <p className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                {patientEligibility.eligibility_status === "hospice_eligible" && "Patient meets hospice eligibility criteria"}
                {patientEligibility.eligibility_status === "approaching_threshold" && (
                  <>Approaching threshold - ~{patientEligibility.days_to_threshold || "N/A"} days</>
                )}
                {patientEligibility.eligibility_status === "above_threshold" && "Above hospice threshold"}
              </p>
            </div>

            {/* LCD Category */}
            {patientEligibility.lcd_category && (
              <div className="mt-2">
                <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>LCD Category: </span>
                <Badge variant="outline" className="text-xs capitalize">
                  {patientEligibility.lcd_category.replace(/_/g, " ")}
                </Badge>
              </div>
            )}

            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-3 text-xs"
              onClick={() => navigate(`/lcd-eligibility?patient=${currentPatientId}`)}
            >
              Full Eligibility Check
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        ) : (
          <div className={`p-3 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Select a patient to view eligibility
            </p>
          </div>
        )}
      </div>
    );
  }

  // Compact view for sidebar (Admin/Manager)
  if (compact) {
    return (
      <div className="space-y-3" data-testid="census-eligibility-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Census Eligibility
            </span>
          </div>
          <Badge variant="outline" className="text-xs">
            {summary?.total_census || 0} patients
          </Badge>
        </div>

        {loading ? (
          <div className={`p-4 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
          </div>
        ) : summary ? (
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            {/* Mini distribution bars */}
            <div className="space-y-2">
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80"
                onClick={() => {
                  fetchPatientsByStatus("eligible");
                  setShowDetails(true);
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                    Hospice Eligible
                  </span>
                </div>
                <span className="text-sm font-bold text-amber-400">
                  {summary.eligibility_distribution.hospice_eligible}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80"
                onClick={() => {
                  fetchPatientsByStatus("approaching");
                  setShowDetails(true);
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                    Approaching
                  </span>
                </div>
                <span className="text-sm font-bold text-blue-400">
                  {summary.eligibility_distribution.approaching_threshold}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80"
                onClick={() => {
                  fetchPatientsByStatus("above");
                  setShowDetails(true);
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className={`text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                    Above Threshold
                  </span>
                </div>
                <span className="text-sm font-bold text-emerald-400">
                  {summary.eligibility_distribution.above_threshold}
                </span>
              </div>
            </div>

            {/* Progress bar visualization */}
            <div className="mt-3 h-2 rounded-full bg-slate-700 overflow-hidden flex">
              <div 
                className="bg-amber-400 transition-all"
                style={{ 
                  width: `${(summary.eligibility_distribution.hospice_eligible / summary.total_census) * 100}%` 
                }}
              />
              <div 
                className="bg-blue-400 transition-all"
                style={{ 
                  width: `${(summary.eligibility_distribution.approaching_threshold / summary.total_census) * 100}%` 
                }}
              />
              <div 
                className="bg-emerald-400 transition-all"
                style={{ 
                  width: `${(summary.eligibility_distribution.above_threshold / summary.total_census) * 100}%` 
                }}
              />
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Avg PPS</p>
                <p className={`text-lg font-bold ${
                  summary.functional_status.avg_pps <= 70 ? "text-amber-400" : "text-emerald-400"
                }`}>
                  {summary.functional_status.avg_pps}
                </p>
              </div>
              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Active Alerts</p>
                <p className="text-lg font-bold text-red-400">
                  {summary.alerts.active_critical + summary.alerts.active_high}
                </p>
              </div>
            </div>

            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-xs"
              onClick={() => setShowDetails(true)}
            >
              View Details
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        ) : null}

        {/* Details Sheet */}
        <Sheet open={showDetails} onOpenChange={setShowDetails}>
          <SheetContent side="right" className={`w-[600px] ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
            <SheetHeader>
              <SheetTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Census Eligibility Analytics
              </SheetTitle>
              <SheetDescription>
                {summary?.total_census} patients across all eligibility categories
              </SheetDescription>
            </SheetHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className={isDark ? "bg-slate-800" : ""}>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="breakdown">By LCD</TabsTrigger>
                <TabsTrigger value="patients">Patients</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-4 space-y-4">
                {/* Pie Chart */}
                <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <CardContent className="p-4">
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            dataKey="value"
                            label={({ name, value }) => `${value}`}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomPieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-4 mt-2">
                      {pieData.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {item.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Trend Chart */}
                {trends.length > 0 && (
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">30-Day Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-32">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={trends.slice(-14)}>
                            <Area 
                              type="monotone" 
                              dataKey="hospice_eligible" 
                              stroke={COLORS.eligible}
                              fill={COLORS.eligible}
                              fillOpacity={0.2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <p className={`text-xs text-center mt-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Hospice eligible patients over time
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-amber-400">
                        {summary?.trend?.new_eligible_this_week || 0}
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        New eligible this week
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-red-400">
                        {summary?.functional_status?.rapid_decline_count || 0}
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Rapid decline
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-purple-400">
                        {summary?.trend?.recertification_due || 0}
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Recertification due
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">
                        {summary?.alerts?.pending_evaluation || 0}
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Pending evaluation
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* LCD Breakdown Tab */}
              <TabsContent value="breakdown" className="mt-4">
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {lcdBreakdown.map((lcd, idx) => (
                      <Card key={idx} className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{lcd.icon}</span>
                              <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                                {lcd.name}
                              </span>
                            </div>
                            <Badge variant="outline">
                              {lcd.total_patients} patients
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div>
                              <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                                Eligible
                              </p>
                              <p className="text-lg font-bold text-amber-400">
                                {lcd.hospice_eligible}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                                Avg PPS
                              </p>
                              <p className={`text-lg font-bold ${
                                lcd.avg_pps <= 70 ? "text-amber-400" : "text-emerald-400"
                              }`}>
                                {lcd.avg_pps}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                                Rapid Decline
                              </p>
                              <p className="text-lg font-bold text-red-400">
                                {lcd.rapid_decline}
                              </p>
                            </div>
                          </div>
                          <Progress 
                            value={(lcd.hospice_eligible / lcd.total_patients) * 100} 
                            className="mt-2 h-1"
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Patients Tab */}
              <TabsContent value="patients" className="mt-4">
                <div className="flex gap-2 mb-4">
                  <Button 
                    size="sm" 
                    variant={selectedStatus === "eligible" ? "default" : "outline"}
                    onClick={() => fetchPatientsByStatus("eligible")}
                    className={selectedStatus === "eligible" ? "bg-amber-600" : ""}
                  >
                    Eligible
                  </Button>
                  <Button 
                    size="sm" 
                    variant={selectedStatus === "approaching" ? "default" : "outline"}
                    onClick={() => fetchPatientsByStatus("approaching")}
                    className={selectedStatus === "approaching" ? "bg-blue-600" : ""}
                  >
                    Approaching
                  </Button>
                  <Button 
                    size="sm" 
                    variant={selectedStatus === "above" ? "default" : "outline"}
                    onClick={() => fetchPatientsByStatus("above")}
                    className={selectedStatus === "above" ? "bg-emerald-600" : ""}
                  >
                    Above
                  </Button>
                </div>
                
                <ScrollArea className="h-[350px]">
                  {loadingPatients ? (
                    <div className="py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : patientList.length > 0 ? (
                    <div className="space-y-2">
                      {patientList.map((patient, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-slate-700/50 ${
                            isDark ? "bg-slate-800" : "bg-gray-100"
                          }`}
                          onClick={() => navigate(`/lcd-eligibility?patient=${patient.id}`)}
                        >
                          <div>
                            <p className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                              {patient.name}
                            </p>
                            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                              {patient.lcd} • {patient.days_at_status}d at current status
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className={`text-sm font-bold ${
                                patient.pps <= 70 ? "text-amber-400" : "text-emerald-400"
                              }`}>
                                PPS: {patient.pps}
                              </p>
                              <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                                PPS: {patient.pps}%
                              </p>
                            </div>
                            {patient.trend === "declining" && (
                              <TrendingDown className="w-4 h-4 text-red-400" />
                            )}
                            {patient.trend === "approaching" && (
                              <TrendingDown className="w-4 h-4 text-amber-400" />
                            )}
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <Filter className={`w-8 h-8 mx-auto mb-2 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                      <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Select a status to view patients
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Full dashboard view
  return (
    <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="census-eligibility-dashboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Census Eligibility Overview
            </CardTitle>
            <CardDescription>
              {summary?.total_census || 0} total patients across all eligibility categories
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : summary ? (
          <div className="grid grid-cols-3 gap-6">
            {/* Distribution Chart */}
            <div className="col-span-1">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-4">
                {pieData.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between cursor-pointer hover:opacity-80"
                    onClick={() => {
                      const status = item.name.includes("Eligible") ? "eligible" 
                        : item.name.includes("Approaching") ? "approaching" : "above";
                      fetchPatientsByStatus(status);
                      setShowDetails(true);
                      setActiveTab("patients");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className="font-bold" style={{ color: item.color }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="col-span-2 grid grid-cols-2 gap-4">
              <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Activity className="w-8 h-8 text-blue-400" />
                    <div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Average PPS
                      </p>
                      <p className={`text-2xl font-bold ${
                        summary.functional_status.avg_pps <= 70 ? "text-amber-400" : "text-emerald-400"
                      }`}>
                        {summary.functional_status.avg_pps}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                    <div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Active Alerts
                      </p>
                      <p className="text-2xl font-bold text-red-400">
                        {summary.alerts.active_critical + summary.alerts.active_high}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-8 h-8 text-amber-400" />
                    <div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Rapid Decline
                      </p>
                      <p className="text-2xl font-bold text-amber-400">
                        {summary.functional_status.rapid_decline_count}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Recertification Due
                      </p>
                      <p className="text-2xl font-bold text-purple-400">
                        {summary.trend.recertification_due}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
